import React from "react";
import { getRequest } from "sharedUtils/httpUtils";
import MUIDataTable from "mui-datatables";
import { convertToNumericMonthAndDayAndYearAndTime } from "sharedUtils/timeUtils";
import { ThemeProvider, createTheme } from "@material-ui/core/styles";
import { CheckIconBlack } from "../../icons";
import PenIcon from "components/icons/PenIcon";

const { DateTime } = require("luxon");

class AscendMerchantData extends React.Component {
    constructor(props) {
        super(props);
        this.tableRef = React.createRef();

        this.state = {
            merchants: [],
            merchantsLoading: false,
            MerchantsTable: {
                title: "Affiliate merchants",
                options: {
                    selectableRows: "multiple",
                    rowsPerPage: 100,
                    sortOrder: {
                        name: 'merchantName',
                        direction: 'asc'
                    },
                },
                getTheme: () => createTheme({
                    typography: {
                        fontFamily: "'Urbanist', 'sf_pro_displayregular', sans-serif",
                    },
                    overrides: {
                        MUIDataTableBodyRow: {
                            root: {
                                '&:nth-child(odd)': {
                                    backgroundColor: '#F9F9F9'
                                },
                                cursor: 'pointer'
                            }
                        },
                    }
                })
            },
        };
    }

    componentDidMount = () => {
        window.scrollTo({ left: 0, top: 0, behavior: 'auto' });
        this.getMerchants();
    };

    getMerchants = async () => {
        let url = `/api/Merchant/GetAscendMerchants`;

        this.setState({ merchantsLoading: true });

        try {
            const merchants = await getRequest(url);

            this.setState({
                merchants,
                merchantsLoading: false,
            });
        } catch (error) {
            console.error("Error fetching merchants:", error);
            this.setState({ merchants: [], merchantsLoading: false });
        }
    };

    onTabChange = (tab) => {
        this.setState({ tab });
    }

    render() {
        const columns = [
            {
                name: "merchantId",
                label: "Merchant ID",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return value ?? "-";
                    }
                }
            },
            {
                name: "hostname",
                label: "Hostname",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return value ?? "-";
                    }
                }
            },
            {
                name: "name",
                label: "Name",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return value ?? "-";
                    }
                }
            },
            {
                name: "description",
                label: "Description",
                options: {
                    filter: true,
                    sort: false,
                    customBodyRender: (value) => {
                        return (
                            <div
                                style={{
                                    maxHeight: "100px", // Approx 5 lines
                                    width: "300px",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 5,
                                    WebkitBoxOrient: "vertical",
                                    wordBreak: "break-word",
                                }}
                                title={value} // Full text on hover
                            >
                                {value ?? "-"}
                            </div>
                        );
                    }
                }
            },
            {
                name: "logo",
                label: "Logo",
                options: {
                    filter: false,
                    sort: false,
                    customBodyRender: (value) => {
                        return value
                            ? <img src={value} alt="Merchant Logo" style={{ maxWidth: "150px", maxHeight: "150px" }} />
                            : "-";
                    }
                }
            },
            {
                name: "deepLinkUrl",
                label: "Deep Link URL",
                options: {
                    filter: false,
                    sort: false,
                    customBodyRender: (value) => {
                        return value
                            ? <a href={value} target="_blank" rel="noopener noreferrer">{value}</a>
                            : "-";
                    }
                }
            },
            {
                name: "prohibitedStates",
                label: "Prohibited States",
                options: {
                    filter: true,
                    sort: false,
                    customBodyRender: (value) => {
                        return value ?? "-";
                    }
                }
            },
            {
                name: "mobileTracking",
                label: "Mobile Tracking",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return value === "Enabled" ? "✅ Enabled" : "❌ No";
                    }
                }
            },
            {
                name: "lockPeriod",
                label: "Lock Period",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return value ?? "-";
                    }
                }
            },
            {
                name: "category",
                label: "Category",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return value ?? "-";
                    }
                }
            },
            {
                name: "address1",
                label: "Address 1",
                options: {
                    filter: false,
                    sort: false,
                    customBodyRender: (value) => {
                        return value ?? "-";
                    }
                }
            },
            {
                name: "address2",
                label: "Address 2",
                options: {
                    filter: false,
                    sort: false,
                    customBodyRender: (value) => {
                        return value ?? "-";
                    }
                }
            },
            {
                name: "city",
                label: "City",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return value ?? "-";
                    }
                }
            },
            {
                name: "stateCode",
                label: "State Code",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return value ?? "-";
                    }
                }
            },
            {
                name: "zipCode",
                label: "Zip Code",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return value ?? "-";
                    }
                }
            },
            {
                name: "countryCode",
                label: "Country Code",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return value ?? "-";
                    }
                }
            },
            {
                name: "phone",
                label: "Phone",
                options: {
                    filter: false,
                    sort: false,
                    customBodyRender: (value) => {
                        return value ?? "-";
                    }
                }
            },
            {
                name: "website",
                label: "Website",
                options: {
                    filter: false,
                    sort: false,
                    customBodyRender: (value) => {
                        return value
                            ? <a href={value} target="_blank" rel="noopener noreferrer">{value}</a>
                            : "-";
                    }
                }
            },
            {
                name: "contactName",
                label: "Contact Name",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return value ?? "-";
                    }
                }
            },
            {
                name: "email",
                label: "Email",
                options: {
                    filter: false,
                    sort: false,
                    customBodyRender: (value) => {
                        return value
                            ? <a href={`mailto:${value}`}>{value}</a>
                            : "-";
                    }
                }
            },
            {
                name: "currency",
                label: "Currency",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return value ?? "-";
                    }
                }
            },
            {
                name: "status",
                label: "Status",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return value === "joined" ? "🟢 Joined" : "🔴 Inactive";
                    }
                }
            },
            {
                name: "joinDate",
                label: "Join Date",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return value
                            ? new Date(value).toLocaleDateString()
                            : "-";
                    }
                }
            },
            {
                name: "cookieDuration",
                label: "Cookie Duration",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return value ?? "-";
                    }
                }
            },
            {
                name: "percentagePayout",
                label: "Percentage Payout",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return value
                            ? `${value.toFixed(2)}%`
                            : "-";
                    }
                }
            },
            {
                name: "flatPayout",
                label: "Flat Payout",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return value
                            ? `$${value.toFixed(2)}`
                            : "-";
                    }
                }
            },
            {
                name: "deepLinking",
                label: "Deep Linking",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return value === "Yes" ? "✅ Yes" : "❌ No";
                    }
                }
            },
            {
                name: "productFeed",
                label: "Product Feed",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return value === "Yes" ? "✅ Yes" : "❌ No";
                    }
                }
            },
            {
                name: "validDomains",
                label: "Valid Domains",
                options: {
                    filter: true,
                    sort: true,
                }
            }
        ];



        return (
            <div>
                <ThemeProvider theme={this.state.MerchantsTable.getTheme()}>
                    <MUIDataTable
                        title={
                            <div style={{ fontFamily: "'Urbanist', sans-serif" }}>
                                <p style={{ fontSize: '22px', marginTop: "20px" }}>{this.props.affiliateProgramManager?.name + " merchants" ?? "No affiliate selected"}</p>
                            </div>
                        }
                        data={this.state.merchants}
                        columns={columns}
                        options={{
                            ...this.state.MerchantsTable.options,
                            textLabels: {
                                body: { noMatch: this.state.merchantsLoading ? "LOADING..." : "No data available" },
                            },
                            selectableRows: "none",
                        }}
                        ref={this.tableRef}
                    />
                </ThemeProvider>
            </div>
        );
    }
}

export default AscendMerchantData;
